import React, { useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { AccessControlProvider } from "@/contexts/AccessControlContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PostHogErrorBoundary } from "@posthog/react";
import { PostHogIdentifier } from "@/components/PostHogIdentifier";
import { PostHogPageView } from "@/components/PostHogPageView";
import { RefreshCw, Home } from "lucide-react";

// Pages
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Vendors from "./pages/Vendors";
import VendorDetail from "./pages/VendorDetail";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import ProjectSchedule from "./pages/ProjectSchedule";
import PriceBooks from "./pages/PriceBooks";
import PriceBookDetail from "./pages/PriceBookDetail";
import Invoices from "./pages/Invoices";
import InvoiceDetail from "./pages/InvoiceDetail";
import InvoiceWithQuoteBasis from "./pages/InvoiceWithQuoteBasis";
import EditInvoice from "./pages/EditInvoice";
import FieldTickets from "./pages/FieldTickets";
import CompanyProfile from "./pages/CompanyProfile";
import MyProfile from "./pages/MyProfile";
import Privacy from "./pages/Privacy";
import Configuration from "./pages/Configuration";
import ExternalSystemDetail from "./pages/ExternalSystemDetail";
import Usage from "./pages/Usage";
import NotFound from "./pages/NotFound";
import ExtractInvoice from "./pages/tools/ExtractInvoice";
import ValidateInvoice from "./pages/tools/ValidateInvoice";
import EarlyPay from "./pages/tools/EarlyPay";
import PdfViewerPage from "./pages/PdfViewerPage";
import Quoting from "./pages/Quoting";
import QuoteDetail from "./pages/QuoteDetail";
import UploadQuote from "./pages/UploadQuote";
import ApSpeed from "./pages/dashboard/ApSpeed";
import ModelPerformance from "./pages/dashboard/ModelPerformance";
import ContractRenewals from "./pages/dashboard/ContractRenewals";
import SupplierReportCards from "./pages/dashboard/SupplierReportCards";
import PaymentAccuracy from "./pages/dashboard/PaymentAccuracy";
import SecurityUsers from "./pages/security/Users";
import SecurityUserDetail from "./pages/security/UserDetail";
import SecurityGroups from "./pages/security/Groups";
import SecurityGroupDetail from "./pages/security/GroupDetail";
import SecurityPermissions from "./pages/security/Permissions";
import SecurityPermissionDetail from "./pages/security/PermissionDetail";

// Layout
import { AppLayout } from "./components/layout/AppLayout";
import { RequireAccess } from "./components/RequireAccess";
import { Modules, Permissions } from "@prolarus/shared";

function ErrorFallback() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="text-center animate-fade-in">
        <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
          <span className="text-4xl font-bold text-destructive">!</span>
        </div>
        <h1 className="text-3xl font-bold text-foreground mb-3">Something Went Wrong</h1>
        <p className="text-muted-foreground mb-8 max-w-md mx-auto">
          An unexpected error occurred. Please refresh the page to try again.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => window.location.reload()}
            className="btn-primary inline-flex items-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            Refresh Page
          </button>
          <a href="/dashboard" className="btn-secondary inline-flex items-center gap-2">
            <Home className="w-5 h-5" />
            Go to Dashboard
          </a>
        </div>
      </div>
    </div>
  );
}

const App = () => {
  const [queryClient] = useState(() => new QueryClient());

  return (
  <QueryClientProvider client={queryClient}>
    <PostHogErrorBoundary fallback={<ErrorFallback />}>
      <ThemeProvider>
        <AuthProvider>
          <SettingsProvider>
          <AccessControlProvider>
          <PostHogIdentifier />
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <PostHogPageView />
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Landing />} />
                <Route path="/auth" element={<Auth />} />

                {/* Protected App Routes */}
                <Route element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/vendors" element={<Vendors />} />
                  <Route path="/vendors/:id" element={<VendorDetail />} />
                  <Route path="/projects" element={<Projects />} />
                  <Route path="/projects/:id" element={<ProjectDetail />} />
                  <Route path="/projects/:id/schedule" element={<ProjectSchedule />} />
                  <Route path="/price-books" element={<PriceBooks />} />
                  <Route path="/quotes" element={<Quoting />} />
                  <Route path="/quotes/upload" element={<UploadQuote />} />
                  <Route path="/quotes/:id" element={<QuoteDetail />} />
                  <Route path="/price-books/:id" element={<PriceBookDetail />} />
                  <Route path="/invoices" element={
                    <RequireAccess module={Modules.COMPLIANCE} permission={[
                      Permissions.invoice.create.code,
                      Permissions.invoice.read.code,
                      Permissions.invoice.update.code,
                      Permissions.invoice.delete.code,
                    ]}>
                      <Invoices />
                    </RequireAccess>
                  } />
                  <Route path="/invoices/:id" element={
                    <RequireAccess module={Modules.COMPLIANCE} permission={[
                      Permissions.invoice.create.code,
                      Permissions.invoice.read.code,
                      Permissions.invoice.update.code,
                      Permissions.invoice.delete.code,
                    ]}>
                      <InvoiceDetail />
                    </RequireAccess>
                  } />
                  <Route path="/invoices/:id/quote-basis" element={<InvoiceWithQuoteBasis />} />
                  <Route path="/invoices/:id/edit" element={
                    <RequireAccess module={Modules.COMPLIANCE} permission={Permissions.invoice.update.code}>
                      <EditInvoice />
                    </RequireAccess>
                  } />
                  <Route path="/pdf" element={<PdfViewerPage />} />
                  <Route path="/field-tickets" element={<FieldTickets />} />
                  <Route path="/company-profile" element={<CompanyProfile />} />
                  <Route path="/my-profile" element={<MyProfile />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/security/users" element={<SecurityUsers />} />
                  <Route path="/security/users/:id" element={<SecurityUserDetail />} />
                  <Route path="/security/groups" element={<SecurityGroups />} />
                  <Route path="/security/groups/:id" element={<SecurityGroupDetail />} />
                  <Route path="/security/permissions" element={<SecurityPermissions />} />
                  <Route path="/security/permissions/:id" element={<SecurityPermissionDetail />} />
                  <Route path="/configuration" element={<Configuration />} />
                  <Route path="/configuration/external-systems/:id" element={<ExternalSystemDetail />} />
                  <Route path="/usage" element={<Usage />} />
                  <Route path="/tools/extract-invoice" element={
                    <RequireAccess module={Modules.COMPLIANCE} permission={Permissions.invoice.upload.code}>
                      <ExtractInvoice />
                    </RequireAccess>
                  } />
                  <Route path="/tools/validate-invoice" element={
                    <RequireAccess module={Modules.COMPLIANCE} permission={Permissions.invoice.upload.code}>
                      <ValidateInvoice />
                    </RequireAccess>
                  } />
                  <Route path="/tools/early-pay" element={<EarlyPay />} />
                  <Route path="/dashboard/ap-speed" element={<ApSpeed />} />
                  <Route path="/dashboard/model-performance" element={<ModelPerformance />} />
                  <Route path="/dashboard/contract-renewals" element={<ContractRenewals />} />
                  <Route path="/dashboard/supplier-report-cards" element={<SupplierReportCards />} />
                  <Route path="/dashboard/payment-accuracy" element={<PaymentAccuracy />} />
                </Route>

                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
          </AccessControlProvider>
          </SettingsProvider>
        </AuthProvider>
      </ThemeProvider>
    </PostHogErrorBoundary>
  </QueryClientProvider>
  );
};

export default App;
